/**
 * Globe camera flights.
 * - browseFlyTo: two-stage cinematic focus for browsing (Focus on globe).
 * - flyToTarget: fixed-offset shot grammar for preview / record / batch.
 *   Preset is always passed in — never read from UI state here.
 */

import type { GlobeInstance } from "globe.gl";
import { MIN_CAMERA_ALTITUDE } from "@web/features/globe/assets.ts";
import {
  SHOT_MIN_ALTITUDE,
  endAltitudeForAspect,
  type ShotAspect,
  type ShotPresetValues,
} from "@web/features/globe/shotPreset.ts";

export const FOCUS_FLIGHT = {
  outAltitude: 2.85,
  outMs: 800,
  inAltitude: 1.05,
  minAltitude: MIN_CAMERA_ALTITUDE,
  inMs: 1600,
  rippleMaxRadius: 4,
  ripplePropagationSpeed: 3,
  rippleRepeatPeriodMs: 700,
  ripplePulses: 3,
} as const;

const SHOT_RIPPLE = {
  maxR: 2.5,
  propagationSpeed: 4,
  repeatPeriodMs: 450,
  pulses: 1,
} as const;

export type FocusFlightOpts = Partial<typeof FOCUS_FLIGHT>;

export type FlyToTarget = {
  lat: number;
  lng: number;
};

export type ShotPose = { lat: number; lng: number; altitude: number };

export function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

async function waitFrames(n: number): Promise<void> {
  for (let i = 0; i < n; i++) await nextFrame();
}

function applyRipple(
  globe: GlobeInstance,
  target: FlyToTarget,
  kind: "browse" | "shot",
): Promise<void> {
  if (kind === "shot") {
    globe.ringsData([
      {
        lat: target.lat,
        lng: target.lng,
        maxR: SHOT_RIPPLE.maxR,
        propagationSpeed: SHOT_RIPPLE.propagationSpeed,
        repeatPeriod: SHOT_RIPPLE.repeatPeriodMs,
      },
    ]);
    return wait(SHOT_RIPPLE.repeatPeriodMs * SHOT_RIPPLE.pulses).then(() => {
      globe.ringsData([]);
    });
  }
  globe.ringsData([
    {
      lat: target.lat,
      lng: target.lng,
      maxR: FOCUS_FLIGHT.rippleMaxRadius,
      propagationSpeed: FOCUS_FLIGHT.ripplePropagationSpeed,
      repeatPeriod: FOCUS_FLIGHT.rippleRepeatPeriodMs,
    },
  ]);
  return wait(FOCUS_FLIGHT.rippleRepeatPeriodMs * FOCUS_FLIGHT.ripplePulses).then(
    () => {
      globe.ringsData([]);
    },
  );
}

/** Lock orbit controls so damping / minDistance can't fight the flight. */
export function lockShotCamera(globe: GlobeInstance): () => void {
  const controls = globe.controls();
  const prev = {
    enabled: controls.enabled,
    autoRotate: controls.autoRotate,
    enableDamping: controls.enableDamping,
    minDistance: controls.minDistance,
    maxDistance: controls.maxDistance,
  };
  const radius = globe.getGlobeRadius();
  controls.enabled = false;
  controls.autoRotate = false;
  controls.enableDamping = false;
  // Wide band so start (zoomed out) and end (close) are both legal.
  controls.minDistance = radius * (1 + SHOT_MIN_ALTITUDE);
  controls.maxDistance = radius * (1 + 8);
  return () => {
    controls.enabled = prev.enabled;
    controls.autoRotate = prev.autoRotate;
    controls.enableDamping = prev.enableDamping;
    controls.minDistance = prev.minDistance;
    controls.maxDistance = prev.maxDistance;
  };
}

export async function browseFlyTo(
  globe: GlobeInstance,
  target: FlyToTarget,
  opts: FocusFlightOpts = {},
): Promise<void> {
  const cfg = { ...FOCUS_FLIGHT, ...opts };
  const controls = globe.controls();
  controls.autoRotate = false;

  const start = globe.pointOfView();
  globe.pointOfView(
    { lat: start.lat, lng: start.lng, altitude: cfg.outAltitude },
    cfg.outMs,
  );
  await wait(cfg.outMs);

  globe.pointOfView(
    {
      lat: target.lat,
      lng: target.lng,
      altitude: Math.max(cfg.inAltitude, cfg.minAltitude),
    },
    cfg.inMs,
  );
  await wait(cfg.inMs);

  await applyRipple(globe, target, "browse");
}

export function shotPoses(
  target: FlyToTarget,
  preset: ShotPresetValues,
  aspect: ShotAspect,
): { start: ShotPose; end: ShotPose } {
  return {
    start: {
      lat: target.lat + preset.offsetLat,
      lng: target.lng + preset.offsetLng,
      altitude: preset.startAltitude,
    },
    end: {
      lat: target.lat,
      lng: target.lng,
      altitude: endAltitudeForAspect(preset, aspect),
    },
  };
}

function poseDelta(globe: GlobeInstance, pose: ShotPose): number {
  const cur = globe.pointOfView();
  return (
    Math.abs(cur.altitude - pose.altitude) +
    Math.abs(cur.lat - pose.lat) * 0.01 +
    Math.abs(cur.lng - pose.lng) * 0.01
  );
}

/**
 * Hard-cut to start and block until the globe reports that pose.
 * Critical: capture must not begin while still on the previous end framing.
 */
export async function poseAtShotStart(
  globe: GlobeInstance,
  start: ShotPose,
): Promise<void> {
  // Cancel any in-flight tween by setting twice across frames.
  globe.pointOfView(start, 0);
  await waitFrames(2);
  globe.pointOfView(start, 0);
  await waitFrames(2);

  const deadline = performance.now() + 750;
  while (performance.now() < deadline) {
    if (poseDelta(globe, start) < 0.08) break;
    globe.pointOfView(start, 0);
    await waitFrames(1);
  }

  // Final assert — force once more and give the WebGL backbuffer time to swap.
  globe.pointOfView(start, 0);
  await waitFrames(3);
  await wait(100);
  globe.pointOfView(start, 0);
  await waitFrames(2);
}

/** Animate start→end. Assumes camera is already at `start`. */
export async function animateShotFlight(
  globe: GlobeInstance,
  end: ShotPose,
  durationMs: number,
): Promise<void> {
  globe.pointOfView(end, durationMs);
  await wait(durationMs);
  globe.pointOfView(end, 0);
  await waitFrames(1);
}

export async function finishShotArrival(
  globe: GlobeInstance,
  target: FlyToTarget,
  preset: ShotPresetValues,
): Promise<void> {
  if (preset.arrivalRipple) {
    await applyRipple(globe, target, "shot");
  }
}

export async function flyToTarget(
  globe: GlobeInstance,
  target: FlyToTarget,
  preset: ShotPresetValues,
  aspect: ShotAspect,
): Promise<void> {
  const { start, end } = shotPoses(target, preset, aspect);
  await poseAtShotStart(globe, start);
  await animateShotFlight(globe, end, preset.durationMs);
  await finishShotArrival(globe, target, preset);
}
